import { HWS, RESLINK, RLM } from './constants';
import { isBoolean, isNonEmptyString, isOptional, isPlainObject, isString } from './guards';
import type {
  HwsRetargetingCandidateInput,
  ReslinkRetargetingCandidateInput,
  RetargetingCandidateInput,
  RlmRetargetingCandidateInput,
  UnknownHwsInput,
  UnknownReslinkInput,
  UnknownRlmInput,
} from './retargeting.types';

function hasRequiredStrings(value: { displayName?: unknown; imageUrl?: unknown; targetUrl?: unknown }): boolean {
  return isNonEmptyString(value.displayName) && isNonEmptyString(value.imageUrl) && isNonEmptyString(value.targetUrl);
}

// Reslink-specific: accepts a non-empty string OR a non-empty array of non-empty strings.
function isValidReslinkMarshaCode(value: unknown): value is string | string[] {
  if (isNonEmptyString(value)) return true;
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.every(isNonEmptyString);
}

export function isReslinkInput(value: unknown): value is ReslinkRetargetingCandidateInput {
  if (!isPlainObject(value)) return false;
  const input = value as UnknownReslinkInput;
  return (
    input.useCase === RESLINK &&
    isNonEmptyString(input.displayName) &&
    // imageUrl stays a required string but may be empty: imageless events are
    // valid — the homepage card falls back to its generic image.
    isString(input.imageUrl) &&
    isNonEmptyString(input.targetUrl) &&
    isValidReslinkMarshaCode(input.marshaCode) &&
    isOptional(input.groupName, isString) &&
    isOptional(input.eventExpirationDate, isString) &&
    isOptional(input.cancellationFlag, isBoolean)
  );
}

export function isRlmInput(value: unknown): value is RlmRetargetingCandidateInput {
  if (!isPlainObject(value)) return false;
  const input = value as UnknownRlmInput;
  return input.useCase === RLM && hasRequiredStrings(input) && isOptional(input.marshaCode, isString);
}

export function isHwsInput(value: unknown): value is HwsRetargetingCandidateInput {
  if (!isPlainObject(value)) return false;
  const input = value as UnknownHwsInput;
  return input.useCase === HWS && hasRequiredStrings(input) && isOptional(input.marshaCode, isString);
}

export function isValidInput(input: unknown): input is RetargetingCandidateInput {
  return isReslinkInput(input) || isRlmInput(input) || isHwsInput(input);
}
